import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import useRedirectFallback from "@/lib/useRedirectFallback";
import Seo, { breadcrumbSchema, itemListSchema } from "@/components/Seo";
import { useCountdown } from "@/components/UrgencyBanner";
import { LookCard } from "@/components/CompleteTheLook";
import AffiliateNote from "@/components/AffiliateNote";

const STATUS = {
  live: { label: "Live now", cls: "pcs-bg-pink text-white" },
  upcoming: { label: "Coming up", cls: "bg-white text-black" },
  "off-season": { label: "Back next year", cls: "bg-white text-black" },
};

const BigUnit = ({ value, label, testId }) => (
  <div className="brut bg-white text-black px-3 md:px-5 py-2 md:py-3 text-center min-w-[64px] md:min-w-[96px]" data-testid={testId}>
    <div className="font-display text-4xl md:text-6xl leading-none tabular-nums">{value}</div>
    <div className="font-mono uppercase text-[10px] tracking-widest mt-1 opacity-70">{label}</div>
  </div>
);

function Countdown({ endAt }) {
  const left = useCountdown(endAt);
  if (!left) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div className="flex gap-2 md:gap-3" data-testid="season-countdown">
      <BigUnit value={left.d} label="Days" testId="season-days" />
      <BigUnit value={pad(left.h)} label="Hours" testId="season-hours" />
      <BigUnit value={pad(left.m)} label="Mins" testId="season-minutes" />
      <BigUnit value={pad(left.s)} label="Secs" testId="season-seconds" />
    </div>
  );
}

function SeasonHero({ c, count, images }) {
  const st = STATUS[c.status];
  const date = new Date(c.end_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric", timeZone: "America/New_York" });
  return (
    <section className="pcs-bg-blue text-white border-b-2 border-black grain" data-testid="season-hero">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
        <div className="font-mono uppercase text-[11px] tracking-widest mb-4 opacity-80">
          <Link to="/" className="hover:underline">Home</Link><span className="mx-2 opacity-50">/</span>Season
        </div>
        <span className={`brut inline-block px-2 py-0.5 font-mono uppercase text-[10px] tracking-widest mb-4 ${st.cls}`} data-testid="season-status">{st.label}</span>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl tracking-tighter leading-none drop-shadow-[4px_4px_0_rgba(0,0,0,1)]" data-testid="season-title">
          {c.name}
        </h1>
        <p className="mt-4 max-w-2xl text-base md:text-lg" data-testid="season-message">{c.message}</p>
        <div className="mt-8">
          {c.status === "off-season" ? (
            <div className="brut bg-white text-black inline-flex items-center gap-3 px-4 py-3" data-testid="season-back-next-year">
              <CalendarClock className="w-5 h-5" strokeWidth={2.5} />
              <span className="font-bold">{c.repeat_yearly ? `Back next year — countdown ends ${date}` : `This campaign has ended${count ? " — the picks below are still available." : "."}`}</span>
            </div>
          ) : (
            <>
              <div className="font-mono uppercase text-[10px] tracking-widest mb-2 opacity-80">Order deadline · {date}</div>
              <Countdown endAt={c.end_at} />
            </>
          )}
        </div>
        <div className="mt-6 font-mono uppercase text-[11px] tracking-widest opacity-80">{count} hand-picked {count === 1 ? "item" : "items"}</div>
        </div>
        {images.length > 0 && (
          <div className="hidden lg:block lg:col-span-5 relative h-[380px]" data-testid="season-collage">
            {images.slice(0, 3).map((src, i) => (
              <img key={src} src={src} alt="" loading="lazy"
                className="absolute w-56 h-56 object-cover brut-lg bg-white pcs-look-card"
                style={{ ...[{ top: 0, left: "8%", rotate: "-6deg" }, { top: "18%", right: 0, rotate: "5deg" }, { bottom: 0, left: "26%", rotate: "-2deg" }][i], animationDelay: `${i * 120}ms` }} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function Season() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useRedirectFallback(!loading && !c);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/campaigns/${slug}`).then((r) => r.data).catch(() => null),
      api.get(`/campaigns/${slug}/products`).then((r) => r.data).catch(() => []),
    ]).then(([camp, prods]) => {
      setC(camp);
      setItems(prods);
      setLoading(false);
    });
  }, [slug]);

  const schema = useMemo(() => {
    if (!c) return null;
    const list = itemListSchema({ name: `${c.name} costumes & gifts`, url: `/season/${slug}` }, items.slice(0, 30));
    return {
      "@context": "https://schema.org",
      "@graph": [
        breadcrumbSchema([
          { name: "Home", url: `${window.location.origin}/` },
          { name: c.name, url: `${window.location.origin}/season/${slug}` },
        ]),
        ...(list ? [list] : []),
      ],
    };
  }, [c, items, slug]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 font-mono uppercase text-xs tracking-widest opacity-70">Loading...</div>;
  if (!c) return <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 font-display text-4xl" data-testid="season-not-found">Season not found.</div>;

  return (
    <div data-testid={`season-page-${slug}`}>
      <Seo
        title={`${c.name} Costumes & Gifts — Shop Before the Deadline`}
        description={`${c.message} Hand-picked ${c.name.toLowerCase()} costumes and party supplies from Amazon and eBay.`}
        path={`/season/${slug}`}
        image={items[0]?.image_url}
        type="website"
        schema={schema}
      />
      <SeasonHero c={c} count={items.length} images={items.map((p) => p.image_url).filter(Boolean)} />
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16">
        {items.length === 0 ? (
          <div className="brut bg-white p-8 text-center" data-testid="season-empty">
            <div className="font-display text-3xl mb-2">Picks are on the way.</div>
            <p className="text-gray-700 mb-4">We're still curating this season. Browse everything in the meantime.</p>
            <Link to="/" className="brut pcs-bg-pink text-white inline-block px-4 py-2 font-bold uppercase text-sm brut-press">Back home</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="season-grid">
            {items.map((p, i) => <LookCard key={p.slug} p={p} i={Math.min(i, 8)} source="season" />)}
          </div>
        )}
        {items.length > 0 && (
          <AffiliateNote className="mt-6" testId="season-affiliate-note" />
        )}
      </section>
    </div>
  );
}
